import axiosInstance from '@/plugins/axios';
import { Pagina } from '@/types/paginacao/Pagina';
import { RubricaAnaliseLote } from '@/types/rubricas/analise-lote/RubricaAnaliseLote';
import { AnaliseGrauExposicaoRisco } from '@/types/analise/AnaliseGrauExposicaoRisco';
import { GRAUS_RISCO } from '@/constants/ConstantGrauRiscoRubrica';
import { Rubrica } from '@/types/rubricas/Rubrica';
import type {
  ProcessamentoLoteDetalhe,
  ProcessamentoLoteItem,
  ProcessamentoLoteRequest,
  ProcessamentoLoteResponse,
} from '@/types/rubricas/analise-lote/ProcessamentoLote';
import { RubricaSituacao } from '@/constants/rubricas/situacao/RubricaSituacao';
import { AnaliseStatus } from '@/constants/analise/AnaliseStatus';

export interface RubricaLoteFilters {
  search?: string;
  grauRisco?: AnaliseGrauExposicaoRisco[];
  ocorrencia?: number[];
  natureza?: number[];
  tipo?: string[];
  situacao?: RubricaSituacao[];
  cnpjEmpregador?: string[];
  idTabela?: string[];
  excecao?: boolean;
  usoEmFolha?: boolean;
  statusAnalise?: AnaliseStatus[];
}

export interface RubricGroupRequest {
  groupBy: 'DESCRICAO';
  filters: RubricaLoteFilters;
  page: number;
  size: number;
}

export interface RubricGroupCard {
  grupo: {
    chave: 'DESCRICAO';
    valores: { descricao: string | null };
  };
  descricao: string;
  tipoNomes: string[];
  naturezaCodigos: number[];
  totais: {
    rubricas: number;
    naturezas: number;
    empregadores: number;
    paraAnalisar: number;
    analisadas: number;
  };
  totaisPorGrauRisco: Record<(typeof GRAUS_RISCO)[number]['totalKey'], number>;
}

export interface RubricItemRequest {
  grupo: RubricGroupCard['grupo'];
  baseFilters: RubricaLoteFilters;
  itemFilters: RubricaLoteFilters;
  page: number;
  size: number;
}

export type RubricFilterOptionsRequest =
  | { grupo?: never; baseFilters?: never }
  | { grupo: RubricGroupCard['grupo']; baseFilters?: RubricaLoteFilters };

export interface RubricFilterOption<T> {
  value: T;
  label: string;
}

export interface RubricFilterOptions {
  natureza: RubricFilterOption<number>[];
  tipo: RubricFilterOption<string>[];
  ocorrencia: RubricFilterOption<number>[];
  cnpjEmpregador: RubricFilterOption<string>[];
  idTabela: RubricFilterOption<string>[];
  grauRisco: RubricFilterOption<AnaliseGrauExposicaoRisco>[];
  situacao: RubricFilterOption<RubricaSituacao>[];
  statusAnalise: RubricFilterOption<AnaliseStatus>[];
  excecao: RubricFilterOption<boolean>[];
  usoEmFolha: RubricFilterOption<boolean>[];
}

/** Rubricas por status com os mesmos filtros dos grupos; o status da aba é ignorado. */
export interface RubricaLoteContagem {
  paraAnalisar: number;
  analisado: number;
}

export default class ServiceRubricaLote {
  static consultarContagem(filters: RubricaLoteFilters) {
    return axiosInstance.post<RubricaLoteContagem>(
      'rubrica/analises/lote/contagem',
      { filters },
      { signal: new AbortController().signal },
    );
  }

  static consultarGrupos(body: RubricGroupRequest) {
    // Isola a consulta de erros nos filtros, que abortam o sinal global do interceptor.
    return axiosInstance.post<Pagina<RubricaAnaliseLote>>(
      'rubrica/analises/lote/grupos/consultar',
      body,
      {
        signal: new AbortController().signal,
      },
    );
  }

  static consultarFiltros(body: RubricFilterOptionsRequest, signal = new AbortController().signal) {
    return axiosInstance.post<RubricFilterOptions>('rubrica/analises/lote/filtros', body, {
      signal,
    });
  }

  static consultarItens(body: RubricItemRequest) {
    // Um erro 500 aborta o sinal global do interceptor; grupos expandidos sao independentes.
    return axiosInstance.post<Pagina<Rubrica>>(
      'rubrica/analises/lote/grupos/itens/consultar',
      body,
      {
        signal: new AbortController().signal,
      },
    );
  }

  /** Cria o processamento (202). Repetir a mesma chave devolve o lote já criado. */
  static criarProcessamento(body: ProcessamentoLoteRequest, idempotencyKey: string) {
    return axiosInstance.post<ProcessamentoLoteResponse>('rubrica/analises/lote', body, {
      headers: { 'Idempotency-Key': idempotencyKey },
      signal: new AbortController().signal,
    });
  }

  static consultarProcessamento(id: string) {
    return axiosInstance.get<ProcessamentoLoteDetalhe>(`rubrica/analises/lote/${id}`, {
      signal: new AbortController().signal,
    });
  }

  static consultarItensProcessamento(id: string, params: { page: number; size: number }) {
    return axiosInstance.get<Pagina<ProcessamentoLoteItem>>(`rubrica/analises/lote/${id}/itens`, {
      params,
      signal: new AbortController().signal,
    });
  }

  /** Confirma a prévia de uma ACAO_EM_MASSA em AGUARDANDO_CONFIRMACAO. */
  static confirmarProcessamento(id: string) {
    return axiosInstance.post<ProcessamentoLoteDetalhe>(
      `rubrica/analises/lote/${id}/confirmar`,
      undefined,
      { signal: new AbortController().signal },
    );
  }
}
